// Fully Enhanced BillsCredits Component with Firestore, Toggles, and Future Extensions
import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./BillsCredits.css";

const BillsCredits = () => {
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [creditEntries, setCreditEntries] = useState([]);
  const [homes, setHomes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [offices, setOffices] = useState([]);
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState({ businessTotal: 0, personalTotal: 0, incomeTotal: 0 });

  const [newExpense, setNewExpense] = useState({
    name: "",
    amount: "",
    frequency: "one-off",
    category: "business",
    notes: "",
    includeInSummary: true,
    date: new Date().toISOString().split("T")[0],
  });
  const [newIncome, setNewIncome] = useState({
    job: "",
    description: "",
    amount: "",
    recurring: false,
    date: new Date().toISOString().split("T")[0],
  });
  const [newCredit, setNewCredit] = useState({
    accountName: "",
    amount: "",
    usedFor: "",
    category: "business",
    date: new Date().toISOString().split("T")[0],
  });
  const [newHome, setNewHome] = useState({
    name: "",
    mortgage: 0,
    electric: 0,
    water: 0,
    gas: 0,
    includeInSummary: true,
    date: new Date().toISOString().split("T")[0],
  });
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    pay: 0,
    insurance: 0,
    includeInSummary: true,
    date: new Date().toISOString().split("T")[0],
  });
  const [newOffice, setNewOffice] = useState({
    name: "",
    lease: 0,
    electric: 0,
    water: 0,
    internet: 0,
    includeInSummary: true,
    date: new Date().toISOString().split("T")[0],
  });
  const [newDebt, setNewDebt] = useState({
    name: "",
    amount: "",
    frequency: "monthly",
    isPaying: true,
    includeInSummary: true,
    date: new Date().toISOString().split("T")[0],
  });

  const [editIncome, setEditIncome] = useState(null);
  const [editHome, setEditHome] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [editCredit, setEditCredit] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [editOffice, setEditOffice] = useState(null);
  const [editDebt, setEditDebt] = useState(null);

  const [viewType, setViewType] = useState("monthly");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [filteredSummary, setFilteredSummary] = useState({ businessTotal: 0, personalTotal: 0, incomeTotal: 0 });
  const [showProjections, setShowProjections] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const expensesSnap = await getDocs(collection(db, "expenses"));
      const incomeSnap = await getDocs(collection(db, "income"));
      const creditSnap = await getDocs(collection(db, "credits"));
      const homesSnap = await getDocs(collection(db, "homes"));
      const employeesSnap = await getDocs(collection(db, "employees"));
      const officeSnap = await getDocs(collection(db, "offices"));
      const debtsSnap = await getDocs(collection(db, "debts"));
      setExpenses(expensesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIncome(incomeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCreditEntries(creditSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setHomes(homesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setEmployees(employeesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setOffices(officeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setDebts(debtsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchData();
  }, []);

  useEffect(() => {
    const filteredDebts = debts.filter(d => d.includeInSummary && d.isPaying);
    const debtTotal = filteredDebts.reduce((sum, d) => {
      return sum + convertRecurringAmount(d.amount, d.frequency, viewType);
    }, 0);

    const business = 
      expenses.filter(e => e.category === "business" && e.includeInSummary).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
      + employees.filter(e => e.includeInSummary).reduce((sum, e) => sum + parseFloat(e.pay || 0) + parseFloat(e.insurance || 0), 0)
      + offices.filter(o => o.includeInSummary).reduce((sum, o) =>
          sum + parseFloat(o.lease || 0)
              + parseFloat(o.electric || 0)
              + parseFloat(o.water || 0)
              + parseFloat(o.internet || 0), 0)
      + debts.filter(d => d.includeInSummary && d.isPaying).reduce((sum, d) => sum + parseFloat(d.amount || 0), 0)
      + creditEntries.filter(c => c.category === "business").reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

    const personal =
      expenses.filter(e => e.category === "personal" && e.includeInSummary).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
      + homes.filter(h => h.includeInSummary).reduce((sum, h) => sum + parseFloat(h.mortgage || 0) + parseFloat(h.electric || 0) + parseFloat(h.water || 0) + parseFloat(h.gas || 0), 0)
      + creditEntries.filter(c => c.category === "personal").reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

    const incomeTotal = income.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);

    setSummary({ businessTotal: business, personalTotal: personal, incomeTotal });
  }, [expenses, employees, homes, offices, income, debts, creditEntries]);

  const convertRecurringAmount = (amount, frequency, view) => {
    const a = parseFloat(amount || 0);
    if (frequency === "weekly") {
      if (view === "daily") return a / 5; // only divide by 5 for work-week assumption
      if (view === "weekly") return a;
      if (view === "monthly") return a * 4.33;
      if (view === "yearly") return a * 52;
    } else if (frequency === "monthly") {
      if (view === "daily") return (a * 12 / 260); // 260 weekdays in a year
      if (view === "weekly") return a / 4.33;
      if (view === "monthly") return a;
      if (view === "yearly") return a * 12;
    }
    return 0;
  };

  useEffect(() => {
    const target = new Date(endDate);
    const start = new Date(startDate);

    const isInRange = (dateStr) => {
      const entryDate = new Date(dateStr);
      if (entryDate < start || entryDate > target) return false;

      if (viewType === "daily") {
        return entryDate.toDateString() === target.toDateString();
      } else if (viewType === "weekly") {
        const startOfWeek = new Date(target);
        startOfWeek.setDate(target.getDate() - target.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return entryDate >= startOfWeek && entryDate <= endOfWeek;
      } else if (viewType === "monthly") {
        return entryDate.getFullYear() === target.getFullYear() && entryDate.getMonth() === target.getMonth();
      } else if (viewType === "yearly") {
        return entryDate.getFullYear() === target.getFullYear();
      }
      return false;
    };

    const filteredHomes = homes.filter(h => {
      if (!h.includeInSummary) return false;
      return h.date ? isInRange(h.date) : true;
    });

    const filteredEmployees = employees.filter(e => {
      if (!e.includeInSummary) return false;
      return e.date ? isInRange(e.date) : true;
    });

    const filteredOffices = offices.filter(o => {
      if (!o.includeInSummary) return false;
      return o.date ? isInRange(o.date) : true;
    });

    const filteredExpenses = expenses.filter(e =>
      e.includeInSummary && (e.frequency === "one-off" ? isInRange(e.date) : true)
    );

    const filteredIncome = income.filter(i =>
      i.recurring ? true : isInRange(i.date)
    );

    const filteredDebts = debts.filter(d => d.includeInSummary && d.isPaying);

    const filteredCredits = creditEntries.filter(c => {
      if (!c.date) return false;
      const dateMatch = isInRange(c.date);
      return dateMatch;
    });

    // Add credit entries into business and personal filtered totals
    const businessCredits = filteredCredits.filter(c => c.category === "business").reduce((sum, c) => {
      return sum + parseFloat(c.amount || 0);
    }, 0);

    const personalCredits = filteredCredits.filter(c => c.category === "personal").reduce((sum, c) => {
      return sum + parseFloat(c.amount || 0);
    }, 0);

    const business = filteredExpenses.filter(e => e.category === "business").reduce((sum, e) => {
      const amount = e.frequency === "one-off"
        ? parseFloat(e.amount || 0)
        : convertRecurringAmount(e.amount, e.frequency, viewType);
      return sum + amount;
    }, 0)
    + filteredEmployees.reduce((sum, e) => {
      return sum + convertRecurringAmount(e.pay, "weekly", viewType) + convertRecurringAmount(e.insurance, "weekly", viewType);
    }, 0)
    + filteredOffices.reduce((sum, o) => {
      return sum
        + convertRecurringAmount(o.lease, "monthly", viewType)
        + convertRecurringAmount(o.electric, "monthly", viewType)
        + convertRecurringAmount(o.water, "monthly", viewType)
        + convertRecurringAmount(o.internet, "monthly", viewType);
    }, 0)
    + filteredDebts.reduce((sum, d) => {
      return sum + convertRecurringAmount(d.amount, d.frequency, viewType);
    }, 0);

    const personal = filteredExpenses.filter(e => e.category === "personal").reduce((sum, e) => {
      const amount = e.frequency === "one-off"
        ? parseFloat(e.amount || 0)
        : convertRecurringAmount(e.amount, e.frequency, viewType);
      return sum + amount;
    }, 0)
    + filteredHomes.reduce((sum, h) => {
      return sum
        + convertRecurringAmount(h.mortgage, "monthly", viewType)
        + convertRecurringAmount(h.electric, "monthly", viewType)
        + convertRecurringAmount(h.water, "monthly", viewType)
        + convertRecurringAmount(h.gas, "monthly", viewType);
    }, 0);

    const incomeTotal = filteredIncome.reduce((sum, i) => {
      const amount = i.recurring
        ? convertRecurringAmount(i.amount, "weekly", viewType)
        : parseFloat(i.amount || 0);
      return sum + amount;
    }, 0);

    setFilteredSummary({
      businessTotal: business + businessCredits,
      personalTotal: personal + personalCredits,
      incomeTotal: incomeTotal,
    });
  }, [viewType, endDate, startDate, expenses, income, employees, homes, offices, debts, creditEntries]);

  const handleInputChange = (e, setter) => {
    const { name, value, type, checked } = e.target;
    setter(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleGenericInput = (e, setter) => {
    const { name, value, type, checked } = e.target;
    setter(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleAdd = async (collectionName, data, setter, listSetter) => {
    const ref = collection(db, collectionName);
    const docRef = await addDoc(ref, data);
    listSetter(prev => [...prev, { ...data, id: docRef.id }]);
    
    if (collectionName === "homes") setter({ name: "", mortgage: 0, electric: 0, water: 0, gas: 0, includeInSummary: true, date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "employees") setter({ name: "", pay: 0, insurance: 0, includeInSummary: true, date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "expenses") setter({ name: "", amount: "", frequency: "one-off", category: "business", notes: "", includeInSummary: true, date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "income") setter({ job: "", description: "", amount: "", recurring: false, date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "credits") setter({ accountName: "", amount: "", usedFor: "", category: "business", date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "offices") setter({ name: "", lease: 0, electric: 0, water: 0, internet: 0, includeInSummary: true, date: new Date().toISOString().split("T")[0] });
    else if (collectionName === "debts") setter({ name: "", amount: "", frequency: "monthly", isPaying: true, includeInSummary: true, date: new Date().toISOString().split("T")[0] });
  };

  const handleDelete = async (collectionName, id, listSetter) => {
    await deleteDoc(doc(db, collectionName, id));
    listSetter(prev => prev.filter(item => item.id !== id));
  };

  return (
    <div className="bills-credits-page">
      <h1>Bills & Credits Overview</h1>

      <section>
  <h2>Homes</h2>
  <table className="excel-table">
    <thead>
      <tr>
        <th>Name</th>
        <th>Mortgage</th>
        <th>Electric</th>
        <th>Water</th>
        <th>Gas</th>
        <th>Date</th>
        <th>Include in Summary</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><input name="name" value={newHome.name} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input name="mortgage" type="number" value={newHome.mortgage} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input name="electric" type="number" value={newHome.electric} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input name="water" type="number" value={newHome.water} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input name="gas" type="number" value={newHome.gas} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input name="date" type="date" value={newHome.date} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><input type="checkbox" name="includeInSummary" checked={newHome.includeInSummary} onChange={e => handleInputChange(e, setNewHome)} /></td>
        <td><button onClick={() => handleAdd("homes", newHome, setNewHome, setHomes)}>Add</button></td>
      </tr>
      {homes.map(home => (
        <tr key={home.id}>
          {editHome?.id === home.id ? (
            <>
              <td><input name="name" value={editHome.name} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input name="mortgage" type="number" value={editHome.mortgage} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input name="electric" type="number" value={editHome.electric} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input name="water" type="number" value={editHome.water} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input name="gas" type="number" value={editHome.gas} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input name="date" type="date" value={editHome.date} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td><input type="checkbox" name="includeInSummary" checked={editHome.includeInSummary} onChange={e => handleGenericInput(e, setEditHome)} /></td>
              <td>
                <button onClick={async () => {
                  await updateDoc(doc(db, "homes", editHome.id), editHome);
                  setHomes(prev => prev.map(h => h.id === editHome.id ? editHome : h));
                  setEditHome(null);
                }}>Save</button>
                <button onClick={() => setEditHome(null)}>Cancel</button>
              </td>
            </>
          ) : (
            <>
              <td>{home.name}</td>
              <td>${parseFloat(home.mortgage).toFixed(2)}</td>
              <td>${parseFloat(home.electric).toFixed(2)}</td>
              <td>${parseFloat(home.water).toFixed(2)}</td>
              <td>${parseFloat(home.gas).toFixed(2)}</td>
              <td>{home.date}</td>
              <td>{home.includeInSummary ? "✔️" : "❌"}</td>
              <td>
                <button onClick={() => setEditHome(home)}>Edit</button>
                <button onClick={async () => {
                  await deleteDoc(doc(db, "homes", home.id));
                  setHomes(prev => prev.filter(h => h.id !== home.id));
                }}>Delete</button>
              </td>
            </>
          )}
        </tr>
      ))}
    </tbody>
  </table>
</section>

<section>
  <h2>Offices</h2>
  <table className="excel-table">
    <thead>
      <tr>
        <th>Name</th>
        <th>Lease</th>
        <th>Electric</th>
        <th>Water</th>
        <th>Internet</th>
        <th>Date</th>
        <th>Include</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><input name="name" value={newOffice.name} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input name="lease" type="number" value={newOffice.lease} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input name="electric" type="number" value={newOffice.electric} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input name="water" type="number" value={newOffice.water} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input name="internet" type="number" value={newOffice.internet} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input name="date" type="date" value={newOffice.date} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><input type="checkbox" name="includeInSummary" checked={newOffice.includeInSummary} onChange={e => handleInputChange(e, setNewOffice)} /></td>
        <td><button onClick={() => handleAdd("offices", newOffice, setNewOffice, setOffices)}>Add</button></td>
      </tr>

      {offices.map(office => (
        <tr key={office.id}>
          {editOffice?.id === office.id ? (
            <>
              <td><input name="name" value={editOffice.name} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input name="lease" type="number" value={editOffice.lease} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input name="electric" type="number" value={editOffice.electric} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input name="water" type="number" value={editOffice.water} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input name="internet" type="number" value={editOffice.internet} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input name="date" type="date" value={editOffice.date} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td><input type="checkbox" name="includeInSummary" checked={editOffice.includeInSummary} onChange={e => handleGenericInput(e, setEditOffice)} /></td>
              <td>
                <button onClick={async () => {
                  await updateDoc(doc(db, "offices", editOffice.id), editOffice);
                  setOffices(prev => prev.map(o => o.id === editOffice.id ? editOffice : o));
                  setEditOffice(null);
                }}>Save</button>
                <button onClick={() => setEditOffice(null)}>Cancel</button>
              </td>
            </>
          ) : (
            <>
              <td>{office.name}</td>
              <td>${parseFloat(office.lease).toFixed(2)}</td>
              <td>${parseFloat(office.electric).toFixed(2)}</td>
              <td>${parseFloat(office.water).toFixed(2)}</td>
              <td>${parseFloat(office.internet).toFixed(2)}</td>
              <td>{office.date}</td>
              <td>{office.includeInSummary ? "✔️" : "❌"}</td>
              <td>
                <button onClick={() => setEditOffice(office)}>Edit</button>
                <button onClick={async () => {
                  await deleteDoc(doc(db, "offices", office.id));
                  setOffices(prev => prev.filter(o => o.id !== office.id));
                }}>Delete</button>
              </td>
            </>
          )}
        </tr>
      ))}
    </tbody>
  </table>
</section>


      <section>
        <h2>Employees</h2>
        <table className="excel-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Weekly Pay</th>
              <th>Insurance</th>
              <th>Date</th>
              <th>Include in Summary</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input name="name" value={newEmployee.name} onChange={e => handleInputChange(e, setNewEmployee)} /></td>
              <td><input name="pay" type="number" value={newEmployee.pay} onChange={e => handleInputChange(e, setNewEmployee)} /></td>
              <td><input name="insurance" type="number" value={newEmployee.insurance} onChange={e => handleInputChange(e, setNewEmployee)} /></td>
              <td><input name="date" type="date" value={newEmployee.date} onChange={e => handleInputChange(e, setNewEmployee)} /></td>
              <td><input type="checkbox" name="includeInSummary" checked={newEmployee.includeInSummary} onChange={e => handleInputChange(e, setNewEmployee)} /></td>
              <td><button onClick={() => handleAdd("employees", newEmployee, setNewEmployee, setEmployees)}>Add</button></td>
            </tr>
            {employees.map(emp => (
              <tr key={emp.id}>
                {editEmployee?.id === emp.id ? (
                  <>
                    <td><input name="name" value={editEmployee.name} onChange={e => handleGenericInput(e, setEditEmployee)} /></td>
                    <td><input name="pay" type="number" value={editEmployee.pay} onChange={e => handleGenericInput(e, setEditEmployee)} /></td>
                    <td><input name="insurance" type="number" value={editEmployee.insurance} onChange={e => handleGenericInput(e, setEditEmployee)} /></td>
                    <td><input name="date" type="date" value={editEmployee.date} onChange={e => handleGenericInput(e, setEditEmployee)} /></td>
                    <td><input type="checkbox" name="includeInSummary" checked={editEmployee.includeInSummary} onChange={e => handleGenericInput(e, setEditEmployee)} /></td>
                    <td>
                      <button onClick={async () => {
                        await updateDoc(doc(db, "employees", editEmployee.id), editEmployee);
                        setEmployees(prev => prev.map(em => em.id === editEmployee.id ? editEmployee : em));
                        setEditEmployee(null);
                      }}>Save</button>
                      <button onClick={() => setEditEmployee(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{emp.name}</td>
                    <td>${parseFloat(emp.pay).toFixed(2)}</td>
                    <td>${parseFloat(emp.insurance).toFixed(2)}</td>
                    <td>{emp.date}</td>
                    <td>{emp.includeInSummary ? "✔️" : "❌"}</td>
                    <td>
                      <button onClick={() => setEditEmployee(emp)}>Edit</button>
                      <button onClick={async () => {
                        await deleteDoc(doc(db, "employees", emp.id));
                        setEmployees(prev => prev.filter(e => e.id !== emp.id));
                      }}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Expenses</h2>
        <table className="excel-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Amount</th>
              <th>Frequency</th>
              <th>Category</th>
              <th>Date</th>
              <th>Include</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input name="name" value={newExpense.name} onChange={e => handleInputChange(e, setNewExpense)} /></td>
              <td><input name="amount" type="number" value={newExpense.amount} onChange={e => handleInputChange(e, setNewExpense)} /></td>
              <td>
                <select name="frequency" value={newExpense.frequency} onChange={e => handleInputChange(e, setNewExpense)}>
                  <option value="one-off">One-Off</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </td>
              <td>
                <select name="category" value={newExpense.category} onChange={e => handleInputChange(e, setNewExpense)}>
                  <option value="business">Business</option>
                  <option value="personal">Personal</option>
                </select>
              </td>
              <td><input name="date" type="date" value={newExpense.date} onChange={e => handleInputChange(e, setNewExpense)} /></td>
              <td><input type="checkbox" name="includeInSummary" checked={newExpense.includeInSummary} onChange={e => handleInputChange(e, setNewExpense)} /></td>
              <td><input name="notes" value={newExpense.notes} onChange={e => handleInputChange(e, setNewExpense)} /></td>
              <td><button onClick={() => handleAdd("expenses", newExpense, setNewExpense, setExpenses)}>Add</button></td>
            </tr>
            {expenses.map(entry => (
              <tr key={entry.id}>
                {editExpense?.id === entry.id ? (
                  <>
                    <td><input name="name" value={editExpense.name} onChange={e => handleGenericInput(e, setEditExpense)} /></td>
                    <td><input name="amount" type="number" value={editExpense.amount} onChange={e => handleGenericInput(e, setEditExpense)} /></td>
                    <td>
                      <select name="frequency" value={editExpense.frequency} onChange={e => handleGenericInput(e, setEditExpense)}>
                        <option value="one-off">One-Off</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </td>
                    <td>
                      <select name="category" value={editExpense.category} onChange={e => handleGenericInput(e, setEditExpense)}>
                        <option value="business">Business</option>
                        <option value="personal">Personal</option>
                      </select>
                    </td>
                    <td><input name="date" type="date" value={editExpense.date} onChange={e => handleGenericInput(e, setEditExpense)} /></td>
                    <td><input type="checkbox" name="includeInSummary" checked={editExpense.includeInSummary} onChange={e => handleGenericInput(e, setEditExpense)} /></td>
                    <td><input name="notes" value={editExpense.notes} onChange={e => handleGenericInput(e, setEditExpense)} /></td>
                    <td>
                      <button onClick={async () => {
                        await updateDoc(doc(db, "expenses", editExpense.id), editExpense);
                        setExpenses(prev => prev.map(e => e.id === editExpense.id ? editExpense : e));
                        setEditExpense(null);
                      }}>Save</button>
                      <button onClick={() => setEditExpense(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{entry.name}</td>
                    <td>${parseFloat(entry.amount).toFixed(2)}</td>
                    <td>{entry.frequency}</td>
                    <td>{entry.category}</td>
                    <td>{entry.date}</td>
                    <td>{entry.includeInSummary ? "✔️" : "❌"}</td>
                    <td>{entry.notes}</td>
                    <td>
                      <button onClick={() => setEditExpense(entry)}>Edit</button>
                      <button onClick={async () => {
                        await deleteDoc(doc(db, "expenses", entry.id));
                        setExpenses(prev => prev.filter(e => e.id !== entry.id));
                      }}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Income</h2>
        <table className="excel-table">
          <thead>
            <tr>
              <th>Job/Property</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Date</th>
              <th>Recurring</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input name="job" value={newIncome.job} onChange={e => handleInputChange(e, setNewIncome)} /></td>
              <td><input name="amount" type="number" value={newIncome.amount} onChange={e => handleInputChange(e, setNewIncome)} /></td>
              <td><input name="description" value={newIncome.description} onChange={e => handleInputChange(e, setNewIncome)} /></td>
              <td><input name="date" type="date" value={newIncome.date} onChange={e => handleInputChange(e, setNewIncome)} /></td>
              <td><input type="checkbox" name="recurring" checked={newIncome.recurring} onChange={e => handleInputChange(e, setNewIncome)} /></td>
              <td><button onClick={() => handleAdd("income", newIncome, setNewIncome, setIncome)}>Add</button></td>
            </tr>
            {income.map(entry => (
              <tr key={entry.id}>
                {editIncome?.id === entry.id ? (
                  <>
                    <td><input name="job" value={editIncome.job} onChange={e => handleGenericInput(e, setEditIncome)} /></td>
                    <td><input name="amount" type="number" value={editIncome.amount} onChange={e => handleGenericInput(e, setEditIncome)} /></td>
                    <td><input name="description" value={editIncome.description} onChange={e => handleGenericInput(e, setEditIncome)} /></td>
                    <td><input name="date" type="date" value={editIncome.date} onChange={e => handleGenericInput(e, setEditIncome)} /></td>
                    <td><input type="checkbox" name="recurring" checked={editIncome.recurring} onChange={e => handleGenericInput(e, setEditIncome)} /></td>
                    <td>
                      <button onClick={async () => {
                        await updateDoc(doc(db, "income", editIncome.id), editIncome);
                        setIncome(prev => prev.map(i => i.id === editIncome.id ? editIncome : i));
                        setEditIncome(null);
                      }}>Save</button>
                      <button onClick={() => setEditIncome(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{entry.job}</td>
                    <td>${parseFloat(entry.amount).toFixed(2)}</td>
                    <td>{entry.description}</td>
                    <td>{entry.date}</td>
                    <td>{entry.recurring ? "✔️" : "❌"}</td>
                    <td>
                      <button onClick={() => setEditIncome(entry)}>Edit</button>
                      <button onClick={async () => {
                        await deleteDoc(doc(db, "income", entry.id));
                        setIncome(prev => prev.filter(i => i.id !== entry.id));
                      }}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Credit Card / Bank Entries</h2>
        <table className="excel-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Amount</th>
              <th>Used For</th>
              <th>Category</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input name="accountName" value={newCredit.accountName} onChange={e => handleInputChange(e, setNewCredit)} /></td>
              <td><input name="amount" type="number" value={newCredit.amount} onChange={e => handleInputChange(e, setNewCredit)} /></td>
              <td><input name="usedFor" value={newCredit.usedFor} onChange={e => handleInputChange(e, setNewCredit)} /></td>
              <td>
                <select name="category" value={newCredit.category} onChange={e => handleInputChange(e, setNewCredit)}>
                  <option value="business">Business</option>
                  <option value="personal">Personal</option>
                </select>
              </td>
              <td><input name="date" type="date" value={newCredit.date} onChange={e => handleInputChange(e, setNewCredit)} /></td>
              <td><button onClick={() => handleAdd("credits", newCredit, setNewCredit, setCreditEntries)}>Add</button></td>
            </tr>
            {creditEntries.map(entry => (
              <tr key={entry.id}>
                {editCredit?.id === entry.id ? (
                  <>
                    <td><input name="accountName" value={editCredit.accountName} onChange={e => handleGenericInput(e, setEditCredit)} /></td>
                    <td><input name="amount" type="number" value={editCredit.amount} onChange={e => handleGenericInput(e, setEditCredit)} /></td>
                    <td><input name="usedFor" value={editCredit.usedFor} onChange={e => handleGenericInput(e, setEditCredit)} /></td>
                    <td>
                      <select name="category" value={editCredit.category} onChange={e => handleGenericInput(e, setEditCredit)}>
                        <option value="business">Business</option>
                        <option value="personal">Personal</option>
                      </select>
                    </td>
                    <td><input name="date" type="date" value={editCredit.date} onChange={e => handleGenericInput(e, setEditCredit)} /></td>
                    <td>
                      <button onClick={async () => {
                        await updateDoc(doc(db, "credits", editCredit.id), editCredit);
                        setCreditEntries(prev => prev.map(c => c.id === editCredit.id ? editCredit : c));
                        setEditCredit(null);
                      }}>Save</button>
                      <button onClick={() => setEditCredit(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{entry.accountName}</td>
                    <td>${parseFloat(entry.amount).toFixed(2)}</td>
                    <td>{entry.usedFor}</td>
                    <td>{entry.category}</td>
                    <td>{entry.date}</td>
                    <td>
                      <button onClick={() => setEditCredit(entry)}>Edit</button>
                      <button onClick={async () => {
                        await deleteDoc(doc(db, "credits", entry.id));
                        setCreditEntries(prev => prev.filter(c => c.id !== entry.id));
                      }}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Debts</h2>
        <table className="excel-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Amount</th>
              <th>Frequency</th>
              <th>Paying?</th>
              <th>Include?</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input name="name" value={newDebt.name} onChange={e => handleInputChange(e, setNewDebt)} /></td>
              <td><input name="amount" type="number" value={newDebt.amount} onChange={e => handleInputChange(e, setNewDebt)} /></td>
              <td>
                <select name="frequency" value={newDebt.frequency} onChange={e => handleInputChange(e, setNewDebt)}>
                  <option value="full">Full</option>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </td>
              <td><input type="checkbox" name="isPaying" checked={newDebt.isPaying} onChange={e => handleInputChange(e, setNewDebt)} /></td>
              <td><input type="checkbox" name="includeInSummary" checked={newDebt.includeInSummary} onChange={e => handleInputChange(e, setNewDebt)} /></td>
              <td><input name="date" type="date" value={newDebt.date} onChange={e => handleInputChange(e, setNewDebt)} /></td>
              <td><button onClick={() => handleAdd("debts", newDebt, setNewDebt, setDebts)}>Add</button></td>
            </tr>

            {debts.map(debt => (
              <tr key={debt.id}>
                {editDebt?.id === debt.id ? (
                  <>
                    <td><input name="name" value={editDebt.name} onChange={e => handleGenericInput(e, setEditDebt)} /></td>
                    <td><input name="amount" type="number" value={editDebt.amount} onChange={e => handleGenericInput(e, setEditDebt)} /></td>
                    <td>
                      <select name="frequency" value={editDebt.frequency} onChange={e => handleGenericInput(e, setEditDebt)}>
                        <option value="full">Full</option>
                        <option value="monthly">Monthly</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </td>
                    <td><input type="checkbox" name="isPaying" checked={editDebt.isPaying} onChange={e => handleGenericInput(e, setEditDebt)} /></td>
                    <td><input type="checkbox" name="includeInSummary" checked={editDebt.includeInSummary} onChange={e => handleGenericInput(e, setEditDebt)} /></td>
                    <td><input name="date" type="date" value={editDebt.date} onChange={e => handleGenericInput(e, setEditDebt)} /></td>
                    <td>
                      <button onClick={async () => {
                        await updateDoc(doc(db, "debts", editDebt.id), editDebt);
                        setDebts(prev => prev.map(d => d.id === editDebt.id ? editDebt : d));
                        setEditDebt(null);
                      }}>Save</button>
                      <button onClick={() => setEditDebt(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{debt.name}</td>
                    <td>${parseFloat(debt.amount).toFixed(2)}</td>
                    <td>{debt.frequency}</td>
                    <td>{debt.isPaying ? "✔️" : "❌"}</td>
                    <td>{debt.includeInSummary ? "✔️" : "❌"}</td>
                    <td>{debt.date}</td>
                    <td>
                      <button onClick={() => setEditDebt(debt)}>Edit</button>
                      <button onClick={async () => {
                        await deleteDoc(doc(db, "debts", debt.id));
                        setDebts(prev => prev.filter(d => d.id !== debt.id));
                      }}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Summary</h2>

        <div className="summary-controls">
          <h3>Report: {startDate} to {endDate} ({viewType})</h3>
          <div className="view-toggle">
            {["daily", "weekly", "monthly", "yearly"].map(v => (
              <button
                key={v}
                onClick={() => setViewType(v)}
                className={viewType === v ? "active" : ""}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          <label>
            Start Reports From:{" "}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>

          <label style={{ marginLeft: "1em" }}>
            End Report Date:{" "}
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>

          <label style={{ marginLeft: "1em" }}>
            <input
              type="checkbox"
              checked={showProjections}
              onChange={() => setShowProjections(prev => !prev)}
            /> Show Projections
          </label>
        </div>

        <div className="summary-table">
          <p>
            <strong>Business Expenses:</strong> ${filteredSummary.businessTotal.toFixed(2)}{" "}
            {showProjections && <em>(includes projections)</em>}
          </p>
          <p>
            <strong>Personal Expenses:</strong> ${filteredSummary.personalTotal.toFixed(2)}{" "}
            {showProjections && <em>(includes projections)</em>}
          </p>
          <p>
            <strong>Total Income:</strong> ${filteredSummary.incomeTotal.toFixed(2)}{" "}
            {showProjections && <em>(includes projections)</em>}
          </p>
          <p>
            <strong>Net:</strong> ${(filteredSummary.incomeTotal - filteredSummary.businessTotal - filteredSummary.personalTotal).toFixed(2)}{" "}
            {showProjections && <em>(includes projections)</em>}
          </p>
        </div>
      </section>

      <section>
        <h2>Breakdown: Actual vs Projected</h2>

        <table className="excel-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name/Description</th>
              <th>Amount</th>
              <th>Category</th>
              <th>Frequency</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {/* Actual Expenses */}
            {expenses
              .filter(e => e.includeInSummary && e.frequency === "one-off" && new Date(e.date) <= new Date(endDate))
              .map(e => (
                <tr key={e.id}>
                  <td>Actual Expense</td>
                  <td>{e.name}</td>
                  <td>${parseFloat(e.amount).toFixed(2)}</td>
                  <td>{e.category}</td>
                  <td>{e.frequency}</td>
                  <td>{e.date}</td>
                </tr>
              ))}

            {/* Projected Expenses */}
            {showProjections && expenses
              .filter(e => e.includeInSummary && e.frequency !== "one-off")
              .map(e => (
                <tr key={e.id}>
                  <td>Projected Expense</td>
                  <td>{e.name}</td>
                  <td>${convertRecurringAmount(e.amount, e.frequency, viewType).toFixed(2)}</td>
                  <td>{e.category}</td>
                  <td>{e.frequency}</td>
                  <td>Recurring</td>
                </tr>
              ))}

            {/* Actual Income */}
            {income
              .filter(i => !i.recurring && new Date(i.date) <= new Date(endDate))
              .map(i => (
                <tr key={i.id}>
                  <td>Actual Income</td>
                  <td>{i.job}</td>
                  <td>${parseFloat(i.amount).toFixed(2)}</td>
                  <td>-</td>
                  <td>One-Off</td>
                  <td>{i.date}</td>
                </tr>
              ))}

            {/* Projected Income */}
            {showProjections && income
              .filter(i => i.recurring)
              .map(i => (
                <tr key={i.id}>
                  <td>Projected Income</td>
                  <td>{i.job}</td>
                  <td>${convertRecurringAmount(i.amount, "weekly", viewType).toFixed(2)}</td>
                  <td>-</td>
                  <td>Weekly</td>
                  <td>Recurring</td>
                </tr>
              ))}

            {/* Actual Debts */}
            {debts
              .filter(d => d.includeInSummary && d.isPaying && new Date(d.date) <= new Date(endDate))
              .map(d => (
                <tr key={d.id}>
                  <td>Actual Debt</td>
                  <td>{d.name}</td>
                  <td>${parseFloat(d.amount).toFixed(2)}</td>
                  <td>-</td>
                  <td>{d.frequency}</td>
                  <td>{d.date}</td>
                </tr>
              ))}

            {/* Projected Debts */}
            {showProjections && debts
              .filter(d => d.includeInSummary && d.isPaying)
              .map(d => (
                <tr key={d.id}>
                  <td>Projected Debt</td>
                  <td>{d.name}</td>
                  <td>${convertRecurringAmount(d.amount, d.frequency, viewType).toFixed(2)}</td>
                  <td>-</td>
                  <td>{d.frequency}</td>
                  <td>Recurring</td>
                </tr>
              ))}

            {/* Actual Credit Entries */}
            {creditEntries
              .filter(c => new Date(c.date) <= new Date(endDate))
              .map(c => (
                <tr key={c.id}>
                  <td>Actual Credit</td>
                  <td>{c.accountName}</td>
                  <td>${parseFloat(c.amount).toFixed(2)}</td>
                  <td>{c.category}</td>
                  <td>One-Off</td>
                  <td>{c.date}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default BillsCredits;
